/**
 * decorators.ts
 * Copyright (c) 2017, My Coverage Plan, Inc.
 * All rights reserved.
 */

interface Options
{
    opt? : boolean;
}

export type TypeValidatorName = "boolean" | "integer" | "float" | "string"
    | "object" | "nullable";
export type SpecialValidator = {min : number} | {max : number} | {enum : any[]}
    | {array : MessageValidatorName};
export type MessageValidatorName = TypeValidatorName | SpecialValidator;

/**
 * A validator is a set of rules associated with a particular object property,
 * designed for consumption by a validation engine such as the one in {@link
 * IncomingMessage}.
 */
export interface Validator
{
  key : string;
  optional : boolean;
  defaultValue? : any;
  rules : MessageValidatorName[];
  customRules? : Array<(json : any) => void>;
}

/**
 * Ensure the class has all properties necessary for validation initialized.
 * @param cls The decorated class
 */
function makeValidatable (cls : any) : void
{
    if (!Array.isArray(cls.validators))
    {
        cls.validators = [];
    }
}

/**
 * Decorate the class with the properties necessary for the message validator
 * to find it while processing the incoming message stream.
 * @param tag Tag to select
 */
export function validateFor (tag : number) : ClassDecorator
{
    return (constructor : any) => {
        constructor.selfValidates = true;
        constructor.tag = tag;
    };
}

/**
 * Add a validation rule for the decorated property
 * @param rules Atom or list of rules (either K-V pairs or names of types)
 * @param optional Whether the property is optional
 * @param defaultValue The default value to inject if the property is missing
 * @param customRules Callbacks to run in addition to the stock rules
 */
export function validateAs (
    rules : MessageValidatorName | MessageValidatorName[],
    optional : boolean = false,
    defaultValue : any = null,
    customRules : ((json : any) => void) | Array<(json : any) => void> = []
) : PropertyDecorator
{
    if (!Array.isArray(rules))
    {
        rules = [rules];
    }
    if (!Array.isArray(customRules))
    {
        customRules = [customRules];
    }
    return (target : any, key : string) => {
        makeValidatable(target);
        target.validators.push({
            key,
            optional,
            defaultValue,
            rules,
            customRules
        });
    };
}

/**
 * The enum interface is a basic summary of all TypeScript enum types
 */
interface Enum
{
    [val : number] : string;
    // [name : string] : string | number;
}

/**
 * A shortcut decorator that extracts the values of a (non-const) Typescript
 * enum in order to validate against.
 * @param e The TypeScript enum
 */
export function validateTSEnum (e : Enum, isArray : boolean = false)
    : PropertyDecorator
{
    const values : number[] = Object.values(e).filter(
        (v : any) => typeof v === "number") as number[];
    return (
        isArray
            ? validateAs({array: {enum: values}})
            : validateAs({enum: values}));
}

/**
 * Shortcut for validating opaque IDs on the server. (This may gain additional
 * power at some point for tracking known objects in server memory.)
 */
export function validateOpaqueID (
    optional : boolean = false,
    defaultValue : any = null,
    nullable : boolean = false
) : PropertyDecorator
{
    if (nullable)
    {
        return (target : any, key : string) => {
            makeValidatable(target);
            target.validators.push({
                key,
                optional,
                defaultValue,
                rules: [],
                customRules: [(json : any) => {
                    if (json[key] !== null && !Number.isSafeInteger(json[key]))
                    {
                        errorOut("ID must be integer or null!", json[key]);
                    }
                }]
            });
        };
    }
    else
    {
        return validateAs(["integer", {min: 0}], optional, defaultValue);
    }
}

/**
 * Tiny helper fn to log to console and throw a matching error.
 * @param msg Message to present
 * @param obj Object to link in the log
 */
export function errorOut (msg : string, obj? : any) : never
{
  console.error(msg, obj);
  throw new Error(msg);
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/server/validation/decorators.ts